<script>
  import { onDestroy, onMount } from "svelte";
  import {
    explorationMode,
    clearExplorationMode,
    explorationTarget,
    explorationKeys,
    underHypothesis,
    loadExplorationStorage,
    updateExplorationStorage,
    forceRefrechRecs,
    updateMLTStorage,
    loadMLTStorage,
  } from "../editor/exploration/exploration.js";
  import {
    systemLog,
    viewSpecs,
    currentSpec,
    clearSoloUnlock,
    glanceView,
    data,
    dataInfo,
    lossWeight,
    saveToRecStacks,
    lessLikeThisStack,
    SKEY,
    nMoreLikeThis,
    callQueue,
    rationaleLevel,
    updatePreference,
    pyodide,
    clingoObject,
  } from "../editor/storage.js";
  import { renderFronto } from "../fronto/fronto.js";
  import { run, run_small } from "../recommender/run.js";
  import {
    clingoConfig,
    recConfig,
    updateRecConfig,
  } from "../recommender/config.js";
  import { writable } from "svelte/store";
  import Spinner from "./component/spinner.svelte";
  import { PrefKeys } from "../editor/component/preference.js";
  import { afterRender, deviceTypes } from "../editor/component/artboard.js";
  import {
    openSystemTooltip,
    closeSystemTooltip,
    deepcopy,
  } from "../editor/utility.js";
  import { nRecommendations, pDrastic } from "../editor/storage.js";
  import { moveToSetting } from "../editor/direct.js";
  import { compareCallTime, genCallID } from "../editor/callQueue.js";
  import { isRecommenderRule } from "../editor/viewSpec.js";
  import { convert } from "../recommender/convert.js";
  import { rerank, resort } from "../recommender/loss/evaluate.js";

  let recommendations = writable([]);
  let showSide = [],
    evaluator;
  let seeCurrentDesign = true;

  let sourceViewName;
  $viewSpecs.forEach((v) => {
    if (v.uid == $currentSpec.source) sourceViewName = v.artboard.name;
  });

  let time_taken;
  let suggestion_minimized, for_printing, for_tiny;

  const applyExisitngEdits = writable(false);

  function afterUpdateRecs(recs) {
    showSide = [];
    recs.forEach((target, i) => {
      if (target.hidden) return;
      showSide.push(false);
      renderFronto(`#recommendation-view-${i} div`, target.spec, {}).then(
        (result, err) => {
          if (err) console.error(err);
          document.querySelector(`#recommendation-view-${i}`).style.padding =
            "13px";
        }
      );
    });
  }
  let error, is_running;
  function getRecommendations(config = {}) {
    time_taken = undefined;
    error = undefined;
    is_running = true;
    if ($explorationMode === explorationKeys.EXPLORATION) {
      let targetSpec = $viewSpecs.filter(
        (spec) => spec.uid === $explorationTarget
      )[0];
      let screen_type = targetSpec.artboard.screen_type;
      screen_type = screen_type.split("-");
      updateRecConfig("intended_screen_type", screen_type.join("-"));
      updateRecConfig("intended_screen", screen_type[0].toLowerCase());
      updateRecConfig(
        "intended_orient",
        screen_type[1]?.toLowerCase() || "landscape"
      );
      updateRecConfig("artboard_width", targetSpec.artboard.artboard_width);
      updateRecConfig("artboard_height", targetSpec.artboard.artboard_height);
      updateRecConfig("n_big_models", $nRecommendations);
      updateRecConfig("p_drastic", $pDrastic);
      updateRecConfig("n_small_models", 1);
      updateRecConfig("weight", $lossWeight);
      recommendations.set([]);
      let viewSpec = $viewSpecs.filter(
        (spec) =>
          spec.uid !== $explorationTarget && spec.uid === targetSpec.source
      )[0];

      if (viewSpec === undefined) {
        console.warn("No source view exists.");
        error = "Source view is not specified.";
        return;
      }
      if (config?.force_refresh) {
        forceRefrechRecs.set(true);
      }

      let data = loadExplorationStorage(
        viewSpec.uid,
        $explorationTarget,
        compareCallTime(viewSpec.last_updated, targetSpec.last_updated) < 0
          ? targetSpec.last_updated
          : viewSpec.last_updated
      );
      if (data !== null && !$forceRefrechRecs) {
        data.data.forEach((item) => {
          if (
            item.llt_stack_id &&
            queryRecStack(SKEY.LLT, item.llt_stack_id).length == 0
          ) {
            item.hidden = false;
          }
        });
        recommendations.set(data.data);
        updateExplorationStorage(
          viewSpec.uid,
          $explorationTarget,
          data.data,
          data.output_config
        );
        suggestion_minimized =
          data.output_config?.suggestion_minimized || false;
        for_printing = data.output_config?.for_printing || false;
        for_tiny = data.output_config?.for_tiny || false;
        forceRefrechRecs.set(false);
        time_taken = "Imported from the saved recommendations";
        is_running = false;
        $systemLog.log({
          message: "Get recommendations",
          artboard_uid: targetSpec.uid,
          source_uid: viewSpec.uid,
          category: "recommender",
          from_save: true,
          time_taken,
        });
      } else {
        // no up-to-date stored data
        let lltStack = $lessLikeThisStack.filter(
          (d) => d.source === viewSpec.uid && d.target === $explorationTarget
        );
        let targetSpec = $viewSpecs.filter(
          (spec) => spec.uid === $explorationTarget
        )[0];
        let fronto = viewSpec.fronto;
        let original_screen_type = viewSpec.artboard.screen_type;
        original_screen_type = original_screen_type.split("-");
        updateRecConfig("original_screen_type", original_screen_type.join("-"));
        updateRecConfig(
          "original_screen",
          original_screen_type[0].toLowerCase()
        );
        updateRecConfig(
          "original_orient",
          original_screen_type[1].toLowerCase()
        );
        updateRecConfig("device_screen_size", [
          targetSpec.artboard.device_width,
          targetSpec.artboard.device_height,
        ]);
        updateRecConfig("specname", viewSpec.uid);
        updateRecConfig("title", viewSpec.artboard.name);
        updateRecConfig("keep_aspect_ratio", false);
        updateRecConfig("lessLikeThisStack", lltStack);
        if (config?.ignore_minmize_transformation) {
          updateRecConfig("ignore_minmize_transformation", true);
        }

        if (fronto) {
          run(
            fronto,
            {}, // todo update constraints
            {
              clingo: $clingoConfig,
              recommender: $recConfig,
              currentDesign: targetSpec.fronto,
              pyodide: $pyodide,
              clingoObject: $clingoObject,
            }
          )
            .then((result) => {
              recommendations.update((items) => {
                result.items.forEach((item, j) => {
                  item.from = viewSpec.artboard.name;
                  item.level = 1;
                  item.index = (j + 1).toString();
                  item.mlt_running = false;
                  item.mlt_seen = [];
                  item.hidden = false;
                  item.upper = null;
                });
                items.push(...result.items);
                return items;
              });
              evaluator = result.evaluator;
              suggestion_minimized =
                result.output_config.suggestion_minimized || false;
              for_tiny = result.output_config.for_tiny || false;
              for_printing = result.output_config.for_printing || false;
              time_taken = result.time_taken;
              updateExplorationStorage(
                viewSpec.uid,
                $explorationTarget,
                $recommendations,
                { suggestion_minimized, for_printing, for_tiny }
              );
            })
            .then(() => {
              forceRefrechRecs.set(false);
              is_running = false;
              $systemLog.log({
                message: "Get recommendations",
                artboard_uid: targetSpec.uid,
                source_uid: viewSpec.uid,
                category: "recommender",
                time_taken,
                from_save: false,
              });
            });
        } else {
          console.warn("The Source view has no fronto spec.");
          error = "Source view is not specified.";
        }
      }
    }
  }

  async function applyRecommendation(rec) {
    viewSpecs.update((specs) => {
      specs.forEach((s) => {
        if (s.uid === $currentSpec.uid) {
          if ($explorationMode === explorationKeys.EXPLORATION) {
            let name = s.addVersion(rec.rules.transformations, true);
            s.checkoutVersion(name)
              .render($data, $dataInfo, () => {
                afterRender(s);
              })
              .finally(() => {
                $systemLog.log({
                  message: "Apply recommendation",
                  artboard_uid: s.uid,
                  category: "recommender",
                  recommendation: rec.descriptions,
                  mode: explorationKeys.EXPLORATION,
                });
              });
          } else if ($explorationMode === explorationKeys.MORELIKETHIS) {
            let added = deepcopy(s.history);
            added.push(...rec.rules.transformations);
            let name = s.addVersion(added, true);
            s.checkoutVersion(name)
              .render($data, $dataInfo, () => {
                afterRender(s);
              })
              .finally(() => {
                $systemLog.log({
                  message: "Apply recommendation",
                  artboard_uid: s.uid,
                  category: "recommender",
                  recommendation: rec.descriptions,
                  mode: explorationKeys.MORELIKETHIS,
                });
              });
          }
        }
      });
      callQueue.update((queue) => {
        queue.addSystemQueue({
          message: "apply-recommendation",
          ids: [$currentSpec.uid],
        });
        return queue;
      });
      return specs;
    });
    closeExplorationPanel();
  }

  async function branchRecommendation(uid, rec) {
    let rules = [];
    if ($explorationMode === explorationKeys.EXPLORATION) {
      rules = rec.rules.transformations;
    } else if ($explorationMode === explorationKeys.MORELIKETHIS) {
      let targetSpec = $viewSpecs.filter((spec) => spec.uid === uid)[0];
      if (!targetSpec) {
        console.warn("The spec to branch is undefined.");
        return;
      }
      rules = deepcopy(targetSpec.history);
      rules.push(...rec.rules.transformations);
    }
    if (rules.length > 0) {
      $currentSpec.branchOut(rules, true).then((branched_uid) => {
        $systemLog.log({
          message: "Branch recommendations",
          artboard_uid: uid,
          branched_uid,
          category: "recommender",
          recommendation: rec.descriptions,
          mode: $explorationMode,
        });
      });
    }
    closeExplorationPanel();
  }

  async function moreLikeThis(rec, i) {
    time_taken = undefined;
    let strategies = rec.parsed_big?.strategies?.map((s) => s.asp) || [];
    let small_strategies =
      rec.parsed_small?.strategies?.map((s) => s.asp) || [];
    strategies.push(...small_strategies);

    let targetSpec = $viewSpecs.filter(
      (spec) => spec.uid === $explorationTarget
    )[0];
    let viewSpec = $viewSpecs.filter(
      (spec) =>
        spec.uid === targetSpec.source && spec?.uid !== $explorationTarget
    )[0];
    if (viewSpec === undefined) {
      console.warn("No source view exists.");
      return;
    }
    let screen_type = targetSpec.artboard.screen_type;
    screen_type = screen_type.split("-");
    updateRecConfig("intended_screen_type", screen_type.join("-"));
    updateRecConfig("intended_screen", screen_type[0]?.toLowerCase());
    updateRecConfig("intended_orient", screen_type[1]?.toLowerCase());
    updateRecConfig("artboard_width", targetSpec.artboard.artboard_width);
    updateRecConfig("artboard_height", targetSpec.artboard.artboard_height);
    updateRecConfig("p_drastic", $pDrastic);
    updateRecConfig("n_small_models", $nMoreLikeThis);
    updateRecConfig("weight", $lossWeight);
    let lltStack = $lessLikeThisStack.filter(
      (d) => d.source === viewSpec.uid && d.target === $explorationTarget
    );
    let fronto = viewSpec.fronto;
    let original_screen_type = viewSpec.artboard.screen_type;
    original_screen_type = original_screen_type.split("-");
    updateRecConfig("original_screen_type", original_screen_type.join("-"));
    updateRecConfig("original_screen", original_screen_type[0].toLowerCase());
    updateRecConfig("original_orient", original_screen_type[1].toLowerCase());
    updateRecConfig("specname", viewSpec.uid);
    updateRecConfig("title", viewSpec.artboard.name);
    updateRecConfig("keep_aspect_ratio", false);
    updateRecConfig("lessLikeThisStack", lltStack);

    if (fronto) {
      rec.mlt_running = true;
      if ($explorationMode === explorationKeys.EXPLORATION) {
        run_small(
          fronto,
          evaluator,
          rec,
          {
            small: small_strategies,
            observed: $recommendations[i].mlt_seen || [],
          },
          {
            clingo: $clingoConfig,
            recommender: $recConfig,
            pyodide: $pyodide,
            clingoObject: $clingoObject,
          }
        )
          .then((result) => {
            recommendations.update((items) => {
              let jbase = rec.mlt_seen.length;
              result.items.forEach((item, j) => {
                item.from = viewSpec.artboard.name;
                item.level = rec.level + 1;
                item.index = rec.index + "-" + (jbase + j + 1).toString();
                item.mlt_running = false;
                item.mlt_seen = [];
                item.upper = rec.rules.name;
                item.hidden = false;
              });
              items.splice(i + 1, 0, ...result.items);
              items = rerank(items);
              return items;
            });
            $recommendations[i].mlt_seen.push(
              ...result.items.map((item) =>
                item.parsed_small.strategies.map((s) => s.asp)
              )
            );
            updateExplorationStorage(
              viewSpec.uid,
              $explorationTarget,
              $recommendations
            );
            rec.mlt_running = false;
            time_taken = result.time_taken;
          })
          .then(() => {
            let stack_id = genCallID([]);
            saveToRecStacks(SKEY.MLT, "add", [{ stack_id, strategies }]);

            $systemLog.log({
              message: "See similar suggestions",
              artboard_uid: $explorationTarget,
              category: "recommender",
              recommendation: rec.descriptions,
              source_uid: viewSpec.uid,
              stack_id,
              time_taken,
            });
          });
      }
      // else if ($explorationMode === explorationKeys.MORELIKETHIS) {
      //   run_similar(
      //     fronto,
      //     targetSpec.fronto,
      //     evaluator,
      //     {
      //       small: small_strategies,
      //       observed: $recommendations[i].mlt_seen || [],
      //       small_cap: 4,
      //       level: 2,
      //     }, // todo update constraints
      //     { clingo: $clingoConfig, recommender: $recConfig, pyodide: $pyodide }
      //   )
      //     .then((result) => {
      //       recommendations.update((items) => {
      //         let jbase = rec.mlt_seen.length;
      //         result.items.forEach((item, j) => {
      //           item.from = viewSpec.artboard.name;
      //           item.level = rec.level + 1;
      //           item.index = rec.index + "-" + (jbase + j + 1).toString();
      //           item.mlt_running = false;
      //           item.mlt_seen = [];
      //           item.upper = rec.rules.name;
      //           item.hidden = false;
      //         });
      //         items.splice(i + 1, 0, ...result.items);
      //         items = rerank(items);
      //         return items;
      //       });
      //       $recommendations[i].mlt_seen.push(
      //         ...result.items.map((item) =>
      //           item.parsed_small.strategies.map((s) => s.asp)
      //         )
      //       );
      //       updateMLTStorage(
      //         viewSpec.uid,
      //         $explorationTarget,
      //         $recommendations
      //       );
      //       rec.mlt_running = false;
      //       time_taken = result.time_taken;
      //     })
      //     .then(() => {
      //       let stack_id = genCallID([]);
      //       saveToRecStacks(SKEY.MLT, "add", [{ stack_id, strategies }]);

      //       $systemLog.log({
      //         message: "See similar suggestions",
      //         artboard_uid: $explorationTarget,
      //         category: "recommender",
      //         recommendation: rec.descriptions,
      //         source_uid: viewSpec.uid,
      //         stack_id,
      //         time_taken,
      //       });
      //     });
      // }
    } else {
      console.warn("Source view has no fronto spec.");
      return;
    }
  }

  async function lessLikeThis(rec, i) {
    let source = $currentSpec.source;
    let target = $explorationTarget;
    let strategies = $recommendations.map((d) => {
      let bs = d.parsed_big?.strategies?.map((s) => s.asp) || [];
      let ss = d.parsed_small?.strategies?.map((s) => s.asp) || [];
      bs.push(...ss);
      return bs;
    });
    let uncommons = strategies[i].filter((s) =>
      // not included in the others, while skipping itself
      strategies.every((d, j) => (i != j && !d.includes(s)) || i == j)
    );
    let commons = strategies[i].filter((s) =>
      // not included in the others, while skipping itself
      strategies.some((d, j) => i != j && d.includes(s))
    );
    let stack_id = genCallID([]);
    saveToRecStacks(SKEY.LLT, "add", [
      {
        stack_id,
        strategies: strategies[i],
        uncommons,
        commons,
        source,
        target,
      },
    ]);
    recommendations.update((recs) => {
      recs[i].hidden = true;
      recs[i].llt_stack_id = stack_id;
      return recs;
    });
    $systemLog.log({
      message: "Hide a design suggestion",
      artboard_uid: $explorationTarget,
      source_uid: source,
      category: "recommender",
      recommendation: rec.descriptions,
      stack_id,
    });
  }

  function revertLessLikeThis(rec, i) {
    if (rec.llt_stack_id) {
      let source = $currentSpec.source;
      saveToRecStacks(SKEY.LLT, "remove", [
        {
          stack_id: rec.llt_stack_id,
        },
      ]);
      $systemLog.log({
        message: "Revert a hidden suggestion",
        artboard_uid: $explorationTarget,
        source_uid: source,
        category: "recommender",
        recommendation: rec.descriptions,
        stack_id: rec.llt_stack_id,
      });
      recommendations.update((recs) => {
        recs[i].hidden = false;
        delete recs[i].llt_stack_id;
        return recs;
      });
    }
  }

  recommendations.subscribe((recs) => {
    afterUpdateRecs(recs);
    window.recs = recs;
  });

  onMount(() => {
    // recommendations.set([]);
    // getRecommendations();
  });

  onDestroy(() => {
    glanceView.set(false);
    showSide = [];
  });

  currentSpec.subscribe((spec) => {
    if ($explorationTarget !== spec?.uid) {
      // clearExplorationMode("a");
      clearSoloUnlock();
    }
  });

  explorationTarget.subscribe((uid) => {
    if (!uid) {
      clearExplorationMode("b");
      clearSoloUnlock();
    }
  });

  explorationMode.subscribe((mode) => {
    if (mode) {
      recommendations.set([]);
      getRecommendations();
    }
  });

  function updateRationaleLevel(event) {
    let lev = parseInt(event.target.value);
    if (lev >= 0 && lev <= 2) {
      updatePreference(PrefKeys.rationaleLevel, lev);
      $systemLog.log({
        message: "Update the level of rationale",
        category: "preference",
        value: lev,
      });
    }
  }
  const sortCategory = writable("original");
  function updateSort(event) {
    let method = event.target.value;
    sortCategory.set(method);
    recommendations.set(resort($recommendations, method));
    $systemLog.log({
      message: "Update the sorting method",
      category: "recommender_method",
      method,
    });
  }

  function closeExplorationPanel() {
    let target = $explorationTarget;
    let mode = $explorationMode;
    clearExplorationMode("c");
    clearSoloUnlock();
    $systemLog.log({
      message: "Close " + mode,
      artboard_uid: target,
    });
  }

  async function replaceRules(putin) {
    let recs = $recommendations.map((d) => d);
    recs.forEach(async (rec, i) => {
      if (putin) {
        rec._withExisting = true;
        let putin2 = putin.filter((d) => !d.isOriginal);
        rec.rules.transformations.push(...putin2);
      } else {
        rec._withExisting = false;
        rec.rules.transformations = rec.rules.transformations.filter((rule) =>
          isRecommenderRule(rule)
        );
      }
    });
    return recs;
  }
  async function convertAfterReplace(recs, source) {
    for (const rec of recs) {
      rec.spec = await convert(source.fronto, rec.rules, {});
    }
    return recs;
  }

  function updateWithExistingEdits() {
    let manualEdits = $currentSpec.getManualEdits().map((d) => {
      d.category = "manual";
      return d;
    });
    console.log(manualEdits);
    let targetSpec = $viewSpecs.filter(
      (spec) => spec.uid === $explorationTarget
    )[0];
    let viewSpec = $viewSpecs.filter(
      (spec) =>
        spec.uid !== $explorationTarget && spec.uid === targetSpec.source
    )[0];
    replaceRules(manualEdits).then((recs) => {
      convertAfterReplace(recs, viewSpec).then((recs2) => {
        recommendations.set(recs2);
      });
    });
    $systemLog.log({
      message: "Brought existing edits",
      artboard_uid: $explorationTarget,
    });
  }

  function takeoutExistingEdits() {
    let targetSpec = $viewSpecs.filter(
      (spec) => spec.uid === $explorationTarget
    )[0];
    let viewSpec = $viewSpecs.filter(
      (spec) =>
        spec.uid !== $explorationTarget && spec.uid === targetSpec.source
    )[0];
    replaceRules(null).then((recs) => {
      convertAfterReplace(recs, viewSpec).then((recs) => {
        recommendations.set(recs);
      });
    });
    $systemLog.log({
      message: "Took out existing edits",
      artboard_uid: $explorationTarget,
    });
  }
  applyExisitngEdits.subscribe((yes) => {
    if (yes) {
      updateWithExistingEdits();
    } else {
      takeoutExistingEdits();
    }
  });
</script>

<section class="exploration-view">
  <div class="exp-title">
    <h2 class="with-close-button">
      <div>
        {#if $explorationMode === "initial"}
          <i class="bi bi-collection me-1" /> Design suggestions
        {:else if $explorationMode === "exploration"}
          <i class="bi bi-collection me-1" /> Design suggestions
        {:else if $explorationMode === "more-like-this"}
          <i class="bi bi-boxes me-1" /> See similar designs
        {/if} for
        <i
          class={"bi bi-" +
            deviceTypes[$currentSpec.artboard.screen_type].icon +
            " me-1"}
        />
        {$currentSpec.artboard.screen_type}
        <p class="text-sm mb-1 mt-1">
          {#if $explorationMode === "initial"}
            Below are design templates to start with.
          {:else if $explorationMode === "exploration"}
            Below are design suggestions generated from <strong
              >{sourceViewName}</strong
            >.
          {:else if $explorationMode === "more-like-this"}
            Below are design alternatives for <strong
              >{$currentSpec.artboard.name}</strong
            >.
          {/if}
          <!-- Hover an alternative design to see in the artboard. -->
        </p>
      </div>
      <div id="top-right-corner-buttons">
        <button
          type="button"
          class="text-like"
          on:click={() => {
            let target = $explorationTarget;
            let mode = $explorationMode;
            clearExplorationMode("d");
            clearSoloUnlock();
            $systemLog.log({
              message: "Close " + mode,
              artboard_uid: target,
            });
            moveToSetting({
              first: "preference",
              subMenu: "recommender_paramenters",
            });
            $systemLog.log({
              message:
                "Move to manage recommender paramenters from exploration view",
            });
          }}
          on:mouseover={(event) => {
            openSystemTooltip(event, "Manage recommender parameters");
          }}
          on:mousemove={(event) => {
            openSystemTooltip(event, "Manage recommender parameters");
          }}
          on:mouseout={() => {
            closeSystemTooltip();
          }}
          on:focus={(event) => {
            openSystemTooltip(event, "Manage recommender parameters");
          }}
          on:blur={() => {
            closeSystemTooltip();
          }}
        >
          <i class="bi bi-sliders" />
        </button>
        <button
          type="button"
          class="text-like"
          on:click={() => {
            closeExplorationPanel();
          }}
        >
          <i class="bi bi-x-lg" />
        </button>
      </div>
    </h2>
    <div class="d-flex" id="adjustments">
      {#if time_taken}
        <p class="text-sm mt-1">
          {#if time_taken.constructor.name === "Number"}
            <i class="bi bi-clock me-1" />{Math.ceil(time_taken / 100) / 10}
            seconds
          {:else}
            <i class="bi bi-box-arrow-in-down me-1" />{time_taken}
          {/if}
        </p>
      {/if}
      <p class="text-sm ms-3 mt-1">
        <input
          name="applyExisting"
          class="my-0 me-1 d-inline align-middle"
          type="checkbox"
          checked={$applyExisitngEdits}
          on:change={(event) => {
            applyExisitngEdits.set(event.target.checked);
          }}
        /><label
          class="ms-1"
          for="applyExisting"
          on:mouseover={(event) => {
            openSystemTooltip(
              event,
              "Bring my custom edits here, if possible. If unchecked, apply a design suggestions will ignore your existing edits."
            );
          }}
          on:mousemove={(event) => {
            openSystemTooltip(
              event,
              "Bring my custom edits here, if possible."
            );
          }}
          on:focus={(event) => {
            openSystemTooltip(
              event,
              "Bring my custom edits here, if possible."
            );
          }}
          on:mouseout={() => {
            closeSystemTooltip();
          }}
          on:blur={() => {
            closeSystemTooltip();
          }}>Bring my existing edits</label
        >
      </p>
      {#if $recommendations.length > 0 && !error && !is_running}
        <p class="text-sm ms-3">
          Level of description:
          <select
            class="ms-1 align-bottom"
            bind:value={$rationaleLevel}
            on:change={(event) => {
              updateRationaleLevel(event);
            }}
            style="padding: 0.15rem;"
          >
            <option value={0} selected={$rationaleLevel == 0}
              >Overview only</option
            >
            <option value={1} selected={$rationaleLevel == 1}
              >Transformations</option
            >
            <option value={2} selected={$rationaleLevel == 2}
              >Transformations + rationales</option
            >
          </select>
        </p>
        <p class="text-sm ms-3">
          Sort by:
          <select
            class="ms-1 align-bottom"
            bind:value={$sortCategory}
            on:change={(event) => {
              updateSort(event);
            }}
            style="padding: 0.15rem;"
          >
            <option value={"original"} selected={$sortCategory === "original"}
              >Original</option
            >
            <option
              value={"identification"}
              selected={$sortCategory === "identification"}
              >Changes to the identifiability of data points</option
            >
            <option
              value={"comparison"}
              selected={$sortCategory === "comparison"}
              >Changes to the pairwise comparison</option
            >
            <option value={"trend"} selected={$sortCategory === "trend"}
              >Changes to data trend</option
            >
            <option value={"text"} selected={$sortCategory === "text"}
              >Changes to text elements</option
            >
            <option value={"density"} selected={$sortCategory === "density"}
              >Density (overplotting, occupation)</option
            >
          </select>
        </p>
      {/if}
    </div>
  </div>
  <div class="glance-view px-2">
    {#if $recommendations.length == 0 && !error && is_running}
      <div class="dupo-menu-pane spinner-wrap">
        <Spinner />
        <span class="spinner-text"
          >Design suggestions being loaded... This may take up to {Math.ceil(
            10 + 2 * $nRecommendations
          )} seconds. <br />
          It's taking more time here because the recommender (developed using Python
          and Clingo) is running on WASM.</span
        >
      </div>
    {:else if error}
      <div class="dupo-menu-pane spinner-wrap">
        <div style="font-size: 2rem; text-align: center;">
          <i class="bi bi-exclamation-triangle-fill" />
        </div>
        <span class="spinner-text">{error}</span>
      </div>
    {/if}
    {#each $recommendations as rec, i}
      {#if rec.hidden}
        <div class="dupo-menu-pane canceled">
          This suggestion is hidden.
          {#if rec.llt_stack_id}To cancel, click <button
              class="a-like"
              on:click={() => {
                revertLessLikeThis(rec, i);
              }}>here</button
            >.{/if}
        </div>
      {:else}
        <div
          class={"dupo-menu-pane m-0 p-2" +
            (rec.level >= 2 ? " see-similar-item" : "") +
            (rec.level !== $recommendations[i - 1]?.level
              ? " level-change-left"
              : "") +
            (rec.level !== $recommendations[i + 1]?.level
              ? " level-change-right"
              : "")}
          on:mouseover={() => {
            showSide[i] = true;
          }}
          on:focus={() => {
            showSide[i] = true;
          }}
          on:mouseout={() => {
            showSide[i] = false;
          }}
          on:blur={() => {
            showSide[i] = false;
          }}
        >
          <div class="dupo-menu-pane-row">
            {#if rec.is_non_rec}
              <h3 class="h-sub">
                Current Design
                <input
                  type="checkbox"
                  name="seeCurrentDesign"
                  id="seeCurrentDesign"
                  class="my-0 ms-3 me-1 d-inline"
                  bind:value={seeCurrentDesign}
                  bind:checked={seeCurrentDesign}
                />
                <label for="seeCurrentDesign">Show</label>
              </h3>
            {:else}
              <h3 class="h-sub">
                Suggestion {rec.index}
                <i
                  class="bi bi-info-circle ms-1"
                  alt="information icon"
                  on:mouseover={(event) => {
                    if (rec.norm_loss?.rank_rel) {
                      openSystemTooltip(event, rec.norm_loss.strength);
                    }
                  }}
                  on:mousemove={(event) => {
                    if (rec.norm_loss?.rank_rel) {
                      openSystemTooltip(event, rec.norm_loss.strength);
                    }
                  }}
                  on:focus={(event) => {
                    if (rec.norm_loss?.rank_rel) {
                      openSystemTooltip(event, rec.norm_loss.strength);
                    }
                  }}
                  on:mouseout={() => {
                    closeSystemTooltip();
                  }}
                  on:blur={() => {
                    closeSystemTooltip();
                  }}
                />
              </h3>
            {/if}
          </div>

          <div
            class="dupo-menu-pane-row"
            style={rec.is_non_rec && !seeCurrentDesign
              ? "height: 0; opacity: 0;"
              : ""}
          >
            <div class="rec-wrap" id={`rec-wrap-${i}`}>
              {#if !rec.is_non_rec}
                <div
                  class={"action-wrap" +
                    (rec.spec.layout?.height * rec.spec.layout?.width < 90000
                      ? " for-tiny"
                      : "")}
                >
                  <h4 class="h-sub mb-1">
                    <i class="bi bi-lightbulb me-1" />Actions
                  </h4>
                  <div class="d-flex flex-wrap justify-content-between">
                    <button
                      id={`branch-${i}`}
                      type="button"
                      class="rec-action branch"
                      on:mouseover={(event) => {
                        openSystemTooltip(
                          event,
                          "Branch this alternative in a new artboard"
                        );
                      }}
                      on:mousemove={(event) => {
                        openSystemTooltip(
                          event,
                          "Branch this alternative in a new artboard"
                        );
                      }}
                      on:mouseout={() => {
                        closeSystemTooltip();
                      }}
                      on:focus={(event) => {
                        openSystemTooltip(
                          event,
                          "Branch this alternative in a new artboard"
                        );
                      }}
                      on:blur={() => {
                        closeSystemTooltip();
                      }}
                      on:click={() => {
                        branchRecommendation($currentSpec.uid, rec);
                      }}><i class="bi bi-diagram-2 me-1" />Branch</button
                    ><br />
                    <button
                      id={`apply-${i}`}
                      type="button"
                      class="rec-action apply"
                      on:mouseover={(event) => {
                        openSystemTooltip(
                          event,
                          "Replace the current artboard with this alternative"
                        );
                      }}
                      on:mousemove={(event) => {
                        openSystemTooltip(
                          event,
                          "Replace the current artboard with this alternative"
                        );
                      }}
                      on:mouseout={() => {
                        closeSystemTooltip();
                      }}
                      on:focus={(event) => {
                        openSystemTooltip(
                          event,
                          "Replace the current artboard with this alternative"
                        );
                      }}
                      on:blur={() => {
                        closeSystemTooltip();
                      }}
                      on:click={() => {
                        applyRecommendation(rec);
                      }}><i class="bi bi-check2-circle me-1" />Apply</button
                    ><br />
                    {#if rec.level < 2}
                      <button
                        id={`moreLikeThis-${i}`}
                        type="button"
                        class="rec-action more"
                        on:mouseover={(event) => {
                          openSystemTooltip(
                            event,
                            "See more designs similar to this one"
                          );
                        }}
                        on:mousemove={(event) => {
                          openSystemTooltip(
                            event,
                            "See more designs similar to this one"
                          );
                        }}
                        on:mouseout={() => {
                          closeSystemTooltip();
                        }}
                        on:focus={(event) => {
                          openSystemTooltip(
                            event,
                            "See more designs similar to this one"
                          );
                        }}
                        on:blur={() => {
                          closeSystemTooltip();
                        }}
                        on:click={() => {
                          moreLikeThis(rec, i);
                        }}><i class="bi bi-boxes me-1" />See similar</button
                      ><br />
                    {/if}
                    <button
                      id={`lessLikeThis-${i}`}
                      type="button"
                      class="rec-action less"
                      on:mouseover={(event) => {
                        openSystemTooltip(
                          event,
                          "Hide this design from the list and do not suggest this in the future"
                        );
                      }}
                      on:mousemove={(event) => {
                        openSystemTooltip(
                          event,
                          "Hide this design from the list and do not suggest this in the future"
                        );
                      }}
                      on:mouseout={() => {
                        closeSystemTooltip();
                      }}
                      on:focus={(event) => {
                        openSystemTooltip(
                          event,
                          "Hide this design from the list and do not suggest this in the future"
                        );
                      }}
                      on:blur={() => {
                        closeSystemTooltip();
                      }}
                      on:click={() => {
                        lessLikeThis(rec, i);
                      }}><i class="bi bi-eraser me-1" />Hide this</button
                    >
                  </div>
                  {#if $rationaleLevel >= 1 && !rec.is_non_rec}
                    <div class="mt-2">
                      <h4 class="h-sub mb-1">
                        <i class="bi bi-braces me-1" />Descriptions
                      </h4>
                      <ul class="mb-0 ps-3">
                        {#if rec.parsed_big?.rationale?.constructor.name === "Array"}
                          {#each rec.parsed_big?.rationale as rat}
                            <li class="mb-0 rec-desc-item">
                              {rat[0]}{#if $rationaleLevel == 2}&nbsp;{rat[1]}{/if}.
                            </li>
                          {/each}
                        {/if}
                        {#if rec.parsed_small?.rationale?.constructor.name === "Array"}
                          {#each rec.parsed_small?.rationale as rat}
                            <li class="mb-0 rec-desc-item">
                              {rat[0]}{#if $rationaleLevel == 2}&nbsp;{rat[1]}{/if}.
                            </li>
                          {/each}
                        {/if}
                      </ul>
                    </div>
                  {/if}
                </div>
              {/if}
              <div id={`recommendation-view-${i}`} class={"view-wrap off-hypo"}>
                <div style={`transform-origin: 0 0;`} />
                {#if rec.interaction_added?.length > 0}
                  <div class="mt-2 interaction-note">
                    <h4
                      class="h-sub"
                      style="background-color: rgba(255,255,255,0.6)"
                    >
                      Changes to interactions
                    </h4>
                    {#if rec.interaction_added.includes("zoom")}
                      <div class="interaction_added mt-1">
                        <i class="bi bi-joystick me-1" />Zoomable (pinch)
                      </div>
                    {/if}
                    {#if rec.interaction_added.includes("context")}
                      <div class="interaction_added mt-1">
                        <i class="bi bi-joystick me-1" />Context view (brush)
                      </div>
                    {/if}
                    {#if rec.interaction_added.includes("tooltip")}
                      <div class="interaction_added mt-1">
                        <i class="bi bi-joystick me-1" />Tooltips (hover)
                      </div>
                    {/if}
                    {#if rec.interaction_added.includes("tooltip_fixed")}
                      <div class="interaction_added mt-1">
                        <i class="bi bi-joystick me-1" />Tooltips fixed at
                        bottom
                      </div>
                    {/if}
                  </div>
                {/if}
              </div>
            </div>
          </div>
        </div>
        {#if rec.mlt_running}
          <div class="dupo-menu-pane spinner-wrap">
            <Spinner />
            <span class="spinner-text"
              >Design suggestions being loaded... This may take about {Math.ceil(
                20 + 2 * $nMoreLikeThis
              )} seconds.</span
            >
          </div>
        {/if}
      {/if}
    {/each}
    {#if $lessLikeThisStack.length > 0 && $recommendations.length > 0}
      <div class="dupo-menu-pane canceled">
        There may be design suggestions that you have hidden earlier. To cancel,
        go to <button
          class="a-like"
          on:click={() => {
            let target = $explorationTarget;
            let mode = $explorationMode;
            clearExplorationMode("e");
            clearSoloUnlock();
            $systemLog.log({
              message: "Close " + mode,
              artboard_uid: target,
            });
            moveToSetting({
              first: "exploration_history",
              artboard_uid: target,
              back_to: {
                mode,
                artboard_uid: target,
              },
            });
            $systemLog.log({
              message:
                "Move to manage exploration history from exploration view",
            });
          }}><i class="bi bi-receipt me-1" /> Exploration history</button
        >.
      </div>
    {/if}
    {#if suggestion_minimized && $recommendations.length > 0}
      <div class="dupo-menu-pane canceled">
        Due to the minimal difference in the artboard size, minimal changes are
        suggested. If you want to sexplore more drastic changes, <button
          class="a-like"
          on:click={() => {
            getRecommendations({
              ignore_minmize_transformation: true,
              force_refresh: true,
            });
            $systemLog.log({
              message:
                "Run drastic exploration, ignoring minimal transformations due to minimal size difference.",
            });
          }}>click here</button
        >.
      </div>
    {/if}
    {#if for_printing && $recommendations.length > 0}
      <div class="dupo-menu-pane canceled">
        For a printed version, our system suggest minimal changes with size
        adjusted and interaction removed.
      </div>
    {/if}
    {#if for_printing && $recommendations.length == 0}
      <div class="dupo-menu-pane canceled">
        For a printed version, our system suggest minimal changes with size
        adjusted and interaction removed.
      </div>
    {/if}
    {#if for_tiny && $recommendations.length > 0}
      <div class="dupo-menu-pane canceled" style="width: 250px;">
        For a thumbnail or smart watch version, our system suggest minimal
        changes with size adjusted and interaction removed.
      </div>
    {/if}
  </div>
</section>

<style>
  .exp-title {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 9999;
    background: #efefef;
    padding: 1rem;
    border-bottom: 1px solid #dddddd;
    color: #454545;
  }
  .exp-title h2 {
    font-size: 0.9rem;
  }
  .exp-title p {
    margin-bottom: 0;
  }
  .exp-title p.text-sm {
    font-weight: var(--base-font-weight);
    font-size: 0.7rem;
  }
  .side-view {
    padding-top: 1rem;
  }
  /* .side-view .rec-wrap {
    display: flex;
    flex-wrap: nowrap;
    position: relative;
  } */
  .glance-view .rec-wrap {
    display: block;
    /* width: 256px; */
    position: relative;
  }
  .view-wrap {
    position: relative;
    padding: 13px;
    border-radius: var(--default-radius);
    background-color: white;
    border: 1px solid #dddddd;
    overflow: hidden;
    font-family: "Arial";
    font-size: 14px;
  }
  .view-wrap:hover,
  .view-wrap.on-hypo {
    box-shadow: 0 0 0 2px #ffc422, 0 0 3px 0 rgba(0, 0, 0, 0.1),
      0 2px 5px 0 rgba(0, 0, 0, 0.05);
  }
  .event-block {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1500;
  }
  /* .side-view .rec-description {
    width: 172px;
    padding-left: 10px;
  } */
  /* .glance-view .rec-description {
    padding-top: 10px;
  } */
  /* .side-view .rec-description-2 {
    width: 100%;
    padding-top: 10px;
  } */
  /* .glance-view .rec-description-2 {
    width: 256px;
    padding-top: 10px;
  } */
  .rec-desc-item {
    font-family: var(--base-font);
    font-size: 0.75rem;
    color: #454545;
    line-height: 1.25;
    line-break: anywhere;
  }
  .action-wrap {
    display: none;
    position: absolute;
    left: 50%;
    top: 1rem;
    width: 90%;
    z-index: 2000;
    max-width: 340px;
    min-width: 200px;
    transform: translate(-50%, 0);
    padding: 0.75rem;
    border-radius: var(--default-radius);
    background-color: #ffffff;
    border: 1px solid #dddddd;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    transition: 0.15s;
  }
  .action-wrap.for-tiny {
    top: 90%;
  }
  .rec-wrap:hover .action-wrap {
    display: initial;
  }
  .rec-action {
    appearance: none;
    margin-bottom: 0.5rem;
    padding: 0.4rem;
    border: 0;
    border-radius: var(--default-radius);
    font-size: 0.8rem;
    color: #454545;
    line-height: 100%;
    font-weight: var(--semi-bold-font-weight);
  }
  .rec-action.branch:hover {
    background-color: #174c90;
    color: white;
  }
  .rec-action.apply:hover {
    background-color: #00716c;
    color: white;
  }
  .rec-action.more:hover {
    background-color: #6e3589;
    color: white;
  }
  .rec-action.less:hover {
    background-color: #cf741d;
    color: white;
  }
  button.text-like {
    appearance: none;
    background: transparent;
    margin: 0;
    padding: 0;
    border: 0;
  }
  .glance-view {
    display: flex;
    /* width: 80vw; */
    flex-wrap: wrap;
  }
  .spinner-wrap {
    padding-top: 2rem !important;
    padding-bottom: 2rem !important;
    margin: 1rem;
    text-align: center;
    background: #e3e3e3;
    border-radius: var(--default-radius);
  }
  .spinner-text {
    display: block;
    font-size: 0.75rem;
    color: #787878;
    font-weight: 400;
    margin-top: 0.5rem;
  }
  .canceled {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
    margin: 1rem;
    text-align: center;
    background: #e3e3e3;
    border-radius: var(--default-radius);
    font-size: 0.75rem;
    color: #999;
    font-weight: 400;
  }
  button.a-like {
    appearance: none;
    display: inline;
    border: 0;
    padding: 0;
    margin: 0;
    line-height: inherit;
    color: inherit;
    font-size: inherit;
    text-decoration: underline;
    background: transparent;
  }
  .interaction-note {
    position: absolute;
    bottom: 0.5rem;
    left: 0.5rem;
  }
  .interaction_added {
    padding: 0.25rem 0.5rem;
    border-radius: var(--default-radius);
    background-color: #ffe8a8;
    border: 1px solid rgba(255, 196, 34, 1);
    font-size: 0.75rem;
  }
  .rationale_summary_vis {
    display: inline-block;
    font-size: 0.75rem;
    color: #454545;
  }

  .see-similar-item {
    background-color: rgba(0, 0, 0, 0.05);
  }
  .see-similar-item.level-change-left {
    border-radius: var(--default-radius) 0 0 0;
  }
  .see-similar-item.level-change-right {
    border-radius: 0 0 var(--default-radius) 0;
  }
</style>
